import { Link } from '@tanstack/react-router';
import { Clock, ShieldCheck, Truck } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { BadgeStatus } from '@/componentes/BadgeStatus';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Medida } from '@/componentes/Medida';
import { formatarAteDuasCasas, formatarDataHora, interpretarDataHora, paraCampoDataHora } from '@/lib/formato';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';
import { tituloDoLancamentoDoParceiro } from '@/modulos/sessao/parceiro';

import { FormularioAbastecimento } from './FormularioAbastecimento';
import { useAbastecimentos, useCadastros, useOrdens } from './consultas';
import { textoDaFaixa, ultimoRegistroDeHorimetro, vizinhosDe } from './consumo';
import { HubDoCombustivel } from './HubDoCombustivel';
import { litrosEscritos } from './litros';
import type { Abastecimento, EquipamentoDoModulo, FaixaConsumo } from './tipos';

/**
 * LANÇAMENTO COMPLETO — o formulário inteiro, para o que não cabe em quatro
 * perguntas.
 *
 * A entrega do dono de 25/10/2026 reduziu o lançamento do dia a dia a quatro
 * respostas: máquina, produto, litros e medidor. Ele vive na aba
 * **Lançamentos**, na linha rápida, ao lado da lista.
 *
 * Esta tela é o resto, e o resto existe:
 *
 *   - o abastecimento **no posto**, que pede preço do litro, número do cupom e
 *     o comprovante;
 *   - a **data retroativa**, de quando o papel chega dois dias depois;
 *   - a **ordem de abastecimento** que o lançamento atende;
 *   - **operador, obra e centro de custo** diferentes do que a alocação diz.
 *
 * Separar as duas não é duplicar: é parar de cobrar onze campos de quem
 * precisa de quatro. Quem precisa dos onze continua tendo todos, com o cartão
 * de contexto ao lado — o último registro do medidor, o abastecimento anterior
 * e a faixa de consumo, que são os três números contra os quais o lançamento
 * é conferido.
 */

export function PaginaLancamentoCompleto() {
  const consultaAbastecimentos = useAbastecimentos();
  const consultaCadastros = useCadastros();
  const sessao = useSessaoEmMemoria();
  /* A PESSOA DE POSTO PARCEIRO (decisão 58, 0132) lança sem ordem de
     abastecimento: a lista das ordens o servidor recusa a ela. */
  const parceiro = sessao?.parceiro ?? null;
  const consultaOrdens = useOrdens(parceiro === null);
  const permissoes = sessao?.permissoes ?? null;
  /* Quem nao lanca nao recebe o formulario (prova do marco, 07/10/2026): ele
     enchia o formulario inteiro e so o envio dizia que faltava a permissao. */
  const podeLancar = permissoes === null || permissoes.includes('abastecimento.registrar');

  const [codigoEscolhido, setCodigoEscolhido] = useState('');
  /* A data e hora do lançamento também é estado da PÁGINA, pelo mesmo motivo do
     equipamento: é ela que decide qual abastecimento é o "anterior", e o cartão
     de contexto ao lado precisa responder a mesma pergunta que o formulário. */
  const [quandoEscolhido, setQuandoEscolhido] = useState(() => paraCampoDataHora(new Date()));

  const abastecimentos = consultaAbastecimentos.data?.linhas ?? [];
  const cadastros = consultaCadastros.data ?? null;
  const equipamentos: readonly EquipamentoDoModulo[] = cadastros?.equipamentos ?? [];
  const equipamento = equipamentos.find((item) => item.codigo === codigoEscolhido) ?? null;
  const vizinhos = vizinhosDe(codigoEscolhido, abastecimentos, interpretarDataHora(quandoEscolhido));
  const ultimoRegistro = ultimoRegistroDeHorimetro(
    equipamento?.ultimaLeitura ?? null,
    vizinhos.anterior,
    vizinhos.proximo === null,
  );
  const faixa = equipamento?.faixa ?? null;
  const ordensAbertas = (consultaOrdens.data ?? []).filter(
    (ordem) => ordem.situacao === 'aberta' && ordem.equipamentoId === (equipamento?.id ?? ''),
  );

  const conteudo = (
    <>
      {/* PARTE A — alimentar o sistema.
          O formulário ocupa a área de conteúdo inteira até XL (R01, p.28: 3
          colunas em L, 2 em M e S, 1 em XS). Os cartões de contexto moram
          ABAIXO dele até 1600 px — ao lado, com o mínimo de 400 px que tinham,
          a grade media o que sobrava e caía para 2 colunas justo em L, e em
          390 o formulário ficava com 640 px e rolava de lado. Só a partir de
          XL eles sobem para a lateral, na largura do painel de detalhe. */}
      <div className="grid grid-cols-1 items-start gap-[var(--grade-espaco)] max-lg:gap-[var(--grade-espaco-estreito)] min-[1600px]:grid-cols-[minmax(0,1fr)_var(--painel-xl)]">
        {!podeLancar ? (
          <div className="cartao min-w-0 p-6">
            <EstadoVazio
              icone={ShieldCheck}
              titulo="Lançar abastecimento pede um perfil que você não tem"
              descricao="Peça a quem administra o sistema a permissão “Lançar abastecimento, próprio ou externo” (abastecimento.registrar) para o seu perfil. A lista de lançamentos, abaixo, continua aberta para leitura."
            />
          </div>
        ) : cadastros === null ? (
          consultaCadastros.isPending ? (
            <EsqueletoDoLancamento />
          ) : (
            /* Separado do carregando: antes, a falha mostrava "Carregando os
               cadastros" para sempre. */
            <div className="cartao min-w-0 p-6">
              <EstadoVazio
                titulo="Não foi possível carregar os cadastros"
                descricao="Sem equipamento, tanque, posto e obra o formulário não abre. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
                acao={
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      void consultaCadastros.refetch();
                    }}
                  >
                    Carregar os cadastros
                  </Botao>
                }
              />
            </div>
          )
        ) : (
          <FormularioAbastecimento
            cadastros={cadastros}
            ordensAbertas={ordensAbertas}
            codigoEscolhido={codigoEscolhido}
            aoEscolherEquipamento={setCodigoEscolhido}
            quando={quandoEscolhido}
            aoMudarQuando={setQuandoEscolhido}
            equipamento={equipamento}
            anterior={vizinhos.anterior}
            proximo={vizinhos.proximo}
            ultimoRegistro={ultimoRegistro}
            faixa={faixa}
          />
        )}

        {/* Os dois cartões ficam lado a lado SÓ entre 1024 e 1599, abaixo do
            formulário — e as duas pontas estão no MESMO variante, em px. Com
            `md:grid-cols-2 min-[1600px]:grid-cols-1` quem decidia era a ordem
            das regras no CSS gerado: o Tailwind escreve o ponto arbitrário
            antes do `md`, e o `md` vencia em 1920 — dois cartões de 188 px
            na coluna de 400, com título, rótulo e data vazando da borda.
            Em XL (a partir de 1600) eles sobem para a lateral, um embaixo do
            outro; abaixo de 1024 empilham na largura inteira, que é quando
            meia largura já não comporta rótulo e valor na mesma linha. Sem
            `items-start`: lado a lado, os dois esticam à mesma altura. */}
        <div className="grid min-w-0 grid-cols-1 gap-[var(--grade-espaco)] max-lg:gap-[var(--grade-espaco-estreito)] min-[1024px]:max-[1600px]:grid-cols-2">
          <CartaoDeContexto
            equipamento={equipamento}
            anterior={vizinhos.anterior}
            ultimoRegistro={ultimoRegistro}
            faixa={faixa}
          />
          {/* A folha por OCR é da empresa, e a tela dela o servidor recusa à
              pessoa de posto. */}
          {parceiro === null ? <CartaoDaFolhaPorOcr /> : null}
        </div>
      </div>
    </>
  );

  /* A pessoa de posto não vê o hub do combustível: os indicadores dele leem
     tanques, ordens e cobranças da empresa, que o servidor recusa a ela, e o
     cabeçalho falaria de um módulo que ela não abre. No lugar, o cabeçalho
     diz de quem são os lançamentos. */
  if (parceiro !== null) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Posto parceiro"
            titulo={tituloDoLancamentoDoParceiro(parceiro.fornecedor)}
            subtitulo="Lance cada abastecimento que o posto fez na frota, com o número do cupom e o comprovante."
          />
          {conteudo}
        </div>
      </div>
    );
  }

  return <HubDoCombustivel>{conteudo}</HubDoCombustivel>;
}

/**
 * O formulário enquanto os cadastros chegam (p.32): na grade e no cartão dele,
 * com o título e os rótulos de verdade — eles não dependem da resposta. Só o
 * controle vira a barra de 40 px; o rótulo do medidor também, porque horímetro
 * ou odômetro é do equipamento escolhido. A forma é a do formulário compacto
 * (30/10/2026): duas linhas de campos, a linha do consumo e a de quem e onde.
 */
function EsqueletoDoLancamento() {
  return (
    <Carregando oQue="Carregando os cadastros do lançamento" className="cartao formulario-grade min-w-0 p-6">
      <h2 className="campo-linha txt-secao text-tinta">Lançar abastecimento</h2>
      {['Tipo', 'Equipamento', 'Data e hora', 'Tanque', 'Litros', null].map((rotulo, i) => (
        <CampoDoEsqueleto key={i} rotulo={rotulo} />
      ))}
      <div className="campo-linha flex items-center gap-3 rounded-controle bloco-rebaixado px-3 py-2">
        <span className="txt-rotulo text-tinta-secundaria">Consumo</span>
        <Barra className="h-3 w-2/5" />
      </div>
      <div className="campo-linha flex flex-wrap items-center gap-3">
        <span className="txt-rotulo-campo">Quem e onde</span>
        <Barra className="h-6 w-28 rounded-etiqueta" />
        <Barra className="h-6 w-36 rounded-etiqueta" />
        <Barra className="h-6 w-32 rounded-etiqueta" />
        <Barra className="h-6 w-40 rounded-etiqueta" />
      </div>
      {/* A barra de salvar do formulário de verdade (R01, regra 15), com os
          botões em barra: o lugar dela já se sabe, os botões vêm com os
          cadastros. */}
      <div className="campo-linha barra-de-acoes flex-wrap">
        <Barra className="h-[var(--controle-m)] w-32 rounded-controle" />
        <Barra className="h-[var(--controle-m)] w-48 rounded-controle" />
      </div>
    </Carregando>
  );
}

function CampoDoEsqueleto({ rotulo }: { readonly rotulo: string | null }) {
  return (
    <span aria-hidden="true" className="flex min-w-0 flex-col gap-2">
      {rotulo === null ? (
        <span className="flex h-4 items-center">
          <Barra className="h-3 w-24" />
        </span>
      ) : (
        <span className="txt-rotulo-campo">{rotulo}</span>
      )}
      <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
    </span>
  );
}

/**
 * O que quem sabe precisa ter na frente enquanto digita.
 *
 * Não é decoração do formulário: é a referência contra a qual os dois números
 * digitados são conferidos. Último registro de medidor, último abastecimento e
 * faixa do modelo são exatamente as três coisas que o encarregado consultaria
 * numa planilha antes de assinar a folha.
 */
function CartaoDeContexto({
  equipamento,
  anterior,
  ultimoRegistro,
  faixa,
}: {
  readonly equipamento: EquipamentoDoModulo | null;
  /** O abastecimento anterior à data digitada. É dele que sai a base do consumo. */
  readonly anterior: Abastecimento | null;
  readonly ultimoRegistro: number | null;
  readonly faixa: FaixaConsumo | null;
}) {
  const unidade = equipamento?.tipoMedidor === 'odometro' ? 'km' : 'h';

  return (
    <div className="cartao flex min-w-0 flex-col gap-3 p-6">
      <div className="flex min-w-0 items-center gap-2">
        {/* Os dois símbolos deste card usam `--icone-menu`: o tokens.css separa
            os tamanhos por CONTEXTO (20 px em menu e card, 16 px em tabela e
            botão), e aqui não há tabela nem botão. */}
        <Truck className="size-[var(--icone-menu)] shrink-0" />
        <h2 className="txt-secao min-w-0 text-pretty text-tinta">
          {equipamento ? equipamento.codigo : 'Contexto do equipamento'}
        </h2>
      </div>

      {equipamento === null ? (
        <p className="txt-apoio text-tinta-secundaria">
          Escolha o equipamento para ver o último registro do medidor, o último abastecimento e a faixa de consumo do
          modelo. É contra esses três números que o lançamento é conferido.
        </p>
      ) : (
        <>
          <span className="txt-apoio text-tinta-secundaria">
            {equipamento.tipo ?? 'Sem tipo'} · {equipamento.modelo ?? 'Sem modelo'}
          </span>
          {equipamento.status === null ? (
            <span className="txt-apoio text-tinta-secundaria">Sem situação registrada</span>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <BadgeStatus status={equipamento.status} />
            </div>
          )}
          {equipamento.motivo ? (
            <span className="txt-apoio text-tinta-secundaria">{equipamento.motivo}</span>
          ) : null}

          <div className="flex flex-col gap-3">
            <DadoDoContexto rotulo="Obra">{equipamento.obra ?? 'Sem alocação'}</DadoDoContexto>
            <DadoDoContexto rotulo="Último registro">
              <span className="inline-flex items-center justify-end gap-2 whitespace-nowrap">
                <Clock className="size-[var(--icone-menu)] shrink-0" />
                {ultimoRegistro === null ? (
                  'Sem registro'
                ) : (
                  <Medida valor={formatarAteDuasCasas(ultimoRegistro)} unidade={unidade} />
                )}
              </span>
            </DadoDoContexto>
            {/* "Anterior", e não "último": com data retroativa o abastecimento
                que serve de base é o anterior ÀQUELA data, não o mais recente
                da máquina.
                Duas linhas que não quebram por dentro: "data · hora" é uma
                unidade, e "litros · medidor", outra (p.12: número com
                unidade; data e hora juntas). Numa frase só, a hora caía
                sozinha na linha de baixo ("20/09/2026 · / 19:01"). */}
            <DadoDoContexto rotulo="Abastecimento anterior">
              {anterior === null ? (
                'Nenhum lançado'
              ) : (
                <>
                  <span className="block whitespace-nowrap">{formatarDataHora(anterior.quando)}</span>
                  <span className="block whitespace-nowrap">
                    <Medida valor={litrosEscritos(anterior.litros)} unidade="L" />
                    {anterior.horimetro === null ? null : (
                      <>
                        {' · '}
                        <Medida valor={formatarAteDuasCasas(anterior.horimetro)} unidade={unidade} />
                      </>
                    )}
                  </span>
                </>
              )}
            </DadoDoContexto>
            {/* "Faixa esperada", e não "do modelo": desde a decisão 8 do dono a
                faixa é da UNIDADE, e a da configuração vale como herança. O
                rótulo antigo mentia justamente na máquina de faixa própria —
                a lista de consumo já foi corrigida, e este era o gêmeo que
                ficou para trás. */}
            <DadoDoContexto rotulo="Faixa esperada">
              <span className="whitespace-nowrap">
                {faixa === null
                  ? 'Sem faixa cadastrada'
                  : (textoDaFaixa(faixa.minimo, faixa.maximo, `L/${unidade}`) ?? 'Sem faixa cadastrada')}
              </span>
            </DadoDoContexto>
          </div>

          {faixa === null ? (
            <p className="txt-apoio text-tinta-apagada">
              Sem faixa na configuração do modelo, o consumo é calculado e mostrado, mas o sistema não avisa se ele
              está fora do esperado. A faixa entra no cadastro do equipamento, no módulo de equipamentos.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Um par rótulo e valor do cartão de contexto: a mesma hierarquia do
 * `DadoChave` do painel (rótulo pequeno no secundário, valor em corpo),
 * mas que QUEBRA DE LINHA quando os dois não cabem lado a lado — o valor desce
 * para baixo do rótulo, ainda à direita. O cartão vive em 400 px na lateral de
 * XL, em meia largura entre 1024 e 1599 e em 358 px no celular; com o rótulo
 * em caixa alta e sem encolher, "Abastecimento anterior" empurrava o valor
 * para fora da borda.
 */
function DadoDoContexto({ rotulo, children }: { readonly rotulo: string; readonly children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <span className="txt-legenda font-normal text-tinta-secundaria">{rotulo}</span>
      <span className="txt-corpo ml-auto min-w-0 text-right text-tinta">{children}</span>
    </div>
  );
}

/**
 * O SEGUNDO CAMINHO, desde a 0035: a folha de campo lida pela IA. O cartão
 * leva até ela, e diz em uma frase como o caminho funciona.
 */
function CartaoDaFolhaPorOcr() {
  return (
    <div className="cartao flex min-w-0 flex-col gap-2 p-6">
      <span className="txt-rotulo text-tinta-secundaria">Segundo caminho de lançamento</span>
      <h2 className="txt-secao text-pretty text-tinta">Folha de campo lida pela IA</h2>
      <p className="txt-corpo text-tinta-secundaria">
        O sistema é de mesa. Quem está na frente de serviço preenche a folha impressa da máquina; quem escaneia confere o
        que a IA leu, olhando o papel, e lança tudo de uma vez.
      </p>
      <div>
        {/* Link no laranja 600, a cor que a p.07 dá ao link. */}
        <Link to="/combustivel/folhas" className="txt-corpo text-marca-texto underline">
          Abrir as folhas de campo
        </Link>
      </div>
    </div>
  );
}
